// Disk-import — scan a connected vault folder and UPSERT one row per
// file into Supabase notes so chat's vault_recall can find pre-existing
// content (the user-connected ChappieBrain case). Runs on connect AND
// on demand from /settings/vault.
//
// Architectural rationale:
//   Fülkit is cloud-canonical-with-disk-mirror. The chat path only
//   handles the chat-creates-the-file direction; pre-existing files in
//   a connected folder were invisible until this module landed.
//   vault_recall queries Supabase only — so we mirror disk → Supabase
//   to make disk content searchable. We persist a LIGHTWEIGHT preview
//   (≤500 chars), not full content. That keeps row size bounded for
//   Obsidian-scale vaults (~10k+ files) while still giving keyword +
//   semantic search enough material to surface the right file.
//
// Idempotency:
//   UPSERT on (user_id, filed_path) — see the partial unique index in
//   scripts/vault-rearchitecture-v2-disk-import.sql. Re-runs are safe;
//   each scan refreshes the preview from current disk state.
//
// Skip rules:
//   _FULKIT/  — Fülkit-managed catalog/ledger/pulse files. Already
//               written by chat path; importing would shadow source-of-
//               truth rows and drift the heat scores.
//   Ah Fulkit/ — Fülkit's brand trunk (welcome kit, VAULT-GUIDE).
//               Same reasoning.
//
// Pure where possible — walker + row builder are testable without FS.
// Network call is isolated in scanAndImportToSupabase.

import { debug } from "./debug";

// last_reviewed: 2026-04-26
export const PREVIEW_MAX_CHARS = 500;

// last_reviewed: 2026-04-26
export const UPSERT_BATCH_SIZE = 100;

// Folders we never import. Lower-cased for case-insensitive compare.
const SKIP_FOLDERS = new Set(["_fulkit", "ah fulkit"]);

// Map file extension → media type for binary detection. Mirrors
// lib/vault-binary.js MIME_EXT but kept separate so this module
// stays import-clean (no top-level binary deps).
const MEDIA_BY_EXT = {
  md: "text/markdown",
  txt: "text/plain",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
  webp: "image/webp",
  heic: "image/heic",
  pdf: "application/pdf",
};

function extOf(name) {
  const idx = name.lastIndexOf(".");
  if (idx < 0) return "";
  return name.slice(idx + 1).toLowerCase();
}

function titleOf(name) {
  const idx = name.lastIndexOf(".");
  return idx < 0 ? name : name.slice(0, idx);
}

function mediaTypeOf(ext) {
  return MEDIA_BY_EXT[ext] || "application/octet-stream";
}

// Walk a vault folder yielding one entry per leaf file. Skips Fülkit-
// managed folders at the TOP LEVEL only (case-insensitive on name).
// Recurses depth-first into all other folders. The async generator
// shape lets the caller stream into batches without buffering the
// entire tree first.
//
// Yields: { filed_path, folder, title, ext, mediaType, fileHandle }
//
// filed_path is vault-relative (no leading slash):
//   "02-BUSINESS/HBBEVCO-LLC/_BUSINESS.md"
// folder is the parent path:
//   "02-BUSINESS/HBBEVCO-LLC"
//   "" for files at the vault root
export async function* walkVaultFolder(directoryHandle, prefix = "") {
  if (!directoryHandle || typeof directoryHandle.values !== "function") return;
  for await (const entry of directoryHandle.values()) {
    if (entry.kind === "directory") {
      // Top-level skip rule only — nested folders named "_FULKIT" are
      // user-created and fair game. The skip set is a denylist for
      // Fülkit-owned trunks at the vault root.
      if (prefix === "" && SKIP_FOLDERS.has(entry.name.toLowerCase())) {
        continue;
      }
      const childPrefix = prefix ? `${prefix}/${entry.name}` : entry.name;
      yield* walkVaultFolder(entry, childPrefix);
    } else if (entry.kind === "file") {
      const ext = extOf(entry.name);
      yield {
        filed_path: prefix ? `${prefix}/${entry.name}` : entry.name,
        folder: prefix,
        title: titleOf(entry.name),
        ext,
        mediaType: mediaTypeOf(ext),
        fileHandle: entry,
      };
    }
  }
}

// Build a notes-table row from a walk entry. Reads the file's bytes
// for .md / .txt to extract a preview; for binaries records placeholder
// content + an attached_files entry so chat sees it.
//
// Uses now() for last_touched if file mtime isn't available — File
// System Access File objects expose .lastModified for this.
//
// Returns the row shape ready for UPSERT into notes:
//   { user_id, title, content, source, folder, filed_path, last_touched,
//     attached_files? }
export async function buildImportRow(entry, userId, now = new Date()) {
  if (!entry || !entry.fileHandle) {
    throw new Error("buildImportRow: missing fileHandle");
  }
  const file = await entry.fileHandle.getFile();
  const isText = entry.ext === "md" || entry.ext === "txt";
  const lastTouched = file.lastModified ? new Date(file.lastModified).toISOString() : now.toISOString();

  // Always include attached_files. The column is NOT NULL DEFAULT '[]'
  // (vault-rearchitecture-v2-step5-multi.sql:17). When a batch upsert
  // contains heterogeneous rows (text + binary), the Supabase JS client
  // builds a column union and includes attached_files in the request.
  // Text rows missing the field send NULL, which the NOT NULL constraint
  // rejects with a 400 — taking the entire batch down. Defaulting to []
  // for text rows keeps every batch's row shape homogeneous.
  if (isText) {
    let text = "";
    try {
      text = await file.text();
    } catch (err) {
      debug.log("vault_disk_import", "read_text_failed", {
        filed_path: entry.filed_path,
        error: err?.message?.slice(0, 200) || "unknown",
      });
      text = "";
    }
    const preview = text.length > PREVIEW_MAX_CHARS
      ? text.slice(0, PREVIEW_MAX_CHARS)
      : text;
    return {
      user_id: userId,
      title: entry.title,
      content: preview,
      source: "disk-import",
      folder: entry.folder || "",
      filed_path: entry.filed_path,
      last_touched: lastTouched,
      attached_files: [],
    };
  }

  // Binary — placeholder content makes the file findable by name +
  // type without buffering bytes. Real recall waits for V2's
  // vault_recall tool (step 12).
  return {
    user_id: userId,
    title: entry.title,
    content: `(binary file: ${entry.filed_path}, type: ${entry.mediaType}, size: ${file.size} bytes)`,
    source: "disk-import",
    folder: entry.folder || "",
    filed_path: entry.filed_path,
    last_touched: lastTouched,
    attached_files: [{
      path: entry.filed_path,
      media_type: entry.mediaType,
      size_bytes: file.size,
      local_only: true,
    }],
  };
}

// Drive the scan: walk → batch → UPSERT. Streams progress via
// opts.onProgress({ scanned, upserted }) so the UI can render a
// live count without us buffering everything first.
//
// Returns: { filesScanned, rowsUpserted, rowsSkipped, errors }
export async function scanAndImportToSupabase(directoryHandle, supabaseClient, userId, opts = {}) {
  if (!directoryHandle || !supabaseClient || !userId) {
    return { filesScanned: 0, rowsUpserted: 0, rowsSkipped: 0, errors: ["missing arg"] };
  }
  const { onProgress = () => {}, batchSize = UPSERT_BATCH_SIZE, now = new Date() } = opts;

  // Lazy-import signal so this module stays server-importable.
  const { emitSignal } = await import("./signal");

  emitSignal("vault_disk_import_started", "info", { batch_size: batchSize });
  debug.log("vault_disk_import", "scan_started", {
    user_id_prefix: String(userId).slice(0, 8),
    batch_size: batchSize,
  });

  let filesScanned = 0;
  let rowsUpserted = 0;
  let rowsSkipped = 0;
  const errors = [];
  let batch = [];

  const flushBatch = async () => {
    if (batch.length === 0) return;
    const { error } = await supabaseClient
      .from("notes")
      .upsert(batch, { onConflict: "user_id,filed_path" });
    if (error) {
      debug.log("vault_disk_import", "batch_upsert_failed", {
        size: batch.length,
        error: error.message?.slice(0, 200) || "unknown",
      });
      errors.push(error.message || "unknown upsert error");
      rowsSkipped += batch.length;
    } else {
      rowsUpserted += batch.length;
      debug.log("vault_disk_import", "batch_upserted", {
        size: batch.length,
        running_total: rowsUpserted,
      });
    }
    batch = [];
    onProgress({ scanned: filesScanned, upserted: rowsUpserted });
  };

  try {
    for await (const entry of walkVaultFolder(directoryHandle)) {
      filesScanned++;
      try {
        const row = await buildImportRow(entry, userId, now);
        batch.push(row);
        debug.log("vault_disk_import", "file_detected", {
          filed_path: entry.filed_path,
          ext: entry.ext,
        });
      } catch (err) {
        debug.log("vault_disk_import", "build_row_failed", {
          filed_path: entry.filed_path,
          error: err?.message?.slice(0, 200) || "unknown",
        });
        errors.push(`${entry.filed_path}: ${err?.message || "build_row_failed"}`);
        rowsSkipped++;
      }
      if (batch.length >= batchSize) {
        await flushBatch();
      }
    }
    await flushBatch();
  } catch (err) {
    debug.log("vault_disk_import", "scan_failed", {
      error: err?.message?.slice(0, 200) || "unknown",
    });
    emitSignal("vault_disk_import_failed", "warning", {
      phase: "walk",
      error: (err?.message || "unknown").slice(0, 200),
    });
    errors.push(err?.message || "scan_failed");
  }

  if (errors.length > 0 && rowsUpserted === 0) {
    emitSignal("vault_disk_import_failed", "warning", {
      phase: "upsert",
      error_count: errors.length,
      first_error: errors[0]?.slice(0, 200) || "unknown",
    });
  }
  emitSignal("vault_disk_import_completed", "info", {
    files_scanned: filesScanned,
    rows_upserted: rowsUpserted,
    rows_skipped: rowsSkipped,
    error_count: errors.length,
  });
  debug.log("vault_disk_import", "scan_completed", {
    files_scanned: filesScanned,
    rows_upserted: rowsUpserted,
    rows_skipped: rowsSkipped,
    error_count: errors.length,
  });

  // Fire-and-forget: trigger embedding backfill for the rows we just
  // upserted. The PUT /api/embed endpoint batches up to 100 unembedded
  // notes per call, sets both `embedding` (vector) and `embedded` (flag).
  // We loop up to 10 rounds (matching the /owner Developer tab button
  // behavior) so a single scan covers up to 1000 fresh rows. Background
  // — caller doesn't await; returning the scan result first keeps the
  // toast snappy. Embedding errors emit vault_disk_import_embed_failed.
  if (rowsUpserted > 0 && opts.triggerEmbed !== false) {
    triggerEmbedBatch(supabaseClient).catch(() => { /* fire-and-forget */ });
  }

  return { filesScanned, rowsUpserted, rowsSkipped, errors };
}

// Async embed trigger — pulled out so tests can stub it via opts and
// scanAndImportToSupabase stays linear. Loops PUT /api/embed up to 10
// rounds (~1000 row capacity), exits early when a batch returns 0.
export async function triggerEmbedBatch(supabaseClient) {
  const { emitSignal } = await import("./signal");
  let session = null;
  try {
    const { data } = await supabaseClient.auth.getSession();
    session = data?.session || null;
  } catch (err) {
    debug.log("vault_disk_import", "embed_session_failed", {
      error: (err?.message || "unknown").slice(0, 200),
    });
    return;
  }
  if (!session?.access_token) {
    debug.log("vault_disk_import", "embed_session_missing", {});
    return;
  }
  emitSignal("vault_disk_import_embed_triggered", "info", {});
  let totalEmbedded = 0;
  let totalFailed = 0;
  for (let round = 0; round < 10; round++) {
    try {
      const res = await fetch("/api/embed", {
        method: "PUT",
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (!res.ok) {
        emitSignal("vault_disk_import_embed_failed", "warning", {
          phase: "http",
          status: res.status,
          round,
        });
        break;
      }
      const data = await res.json();
      totalEmbedded += data.embedded || 0;
      totalFailed += data.failed || 0;
      if ((data.embedded || 0) === 0) break;
    } catch (err) {
      emitSignal("vault_disk_import_embed_failed", "warning", {
        phase: "fetch",
        round,
        error: (err?.message || "unknown").slice(0, 200),
      });
      break;
    }
  }
  debug.log("vault_disk_import", "embed_trigger_completed", {
    total_embedded: totalEmbedded,
    total_failed: totalFailed,
  });
}
